import React, { useState, useCallback, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Delete, Trash2, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import {
  getPredictionEngine,
} from "@/lib/aacPredictionEngine";
import { useTranslation } from "@/hooks/useTranslation";
import { PhysicsEngine } from "@/features/physics/PhysicsEngine";
import { CursorStore } from "@/features/cursor/CursorStore";

// Keyboard layouts - same as AACKeyboard
const hebrewLayout = [
  ["ק", "ר", "א", "ט", "ו", "ן", "ם", "פ"],
  ["ש", "ד", "ג", "כ", "ע", "י", "ח", "ל", "ך", "ף"],
  ["ז", "ס", "ב", "ה", "נ", "מ", "צ", "ת", "ץ"],
];

const englishLayout = [
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["Z", "X", "C", "V", "B", "N", "M"],
];

const russianLayout = [
  ["Й", "Ц", "У", "К", "Е", "Н", "Г", "Ш", "Щ", "З", "Х", "Ъ"],
  ["Ф", "Ы", "В", "А", "П", "Р", "О", "Л", "Д", "Ж", "Э", "Ё"],
  ["Я", "Ч", "С", "М", "И", "Т", "Ь", "Б", "Ю"],
];

const arabicLayout = [
  ["ض", "ص", "ث", "ق", "ف", "غ", "ع", "ه", "خ", "ح"],
  ["ش", "س", "ي", "ب", "ل", "ا", "ت", "ن", "م", "ك"],
  ["ئ", "ء", "ؤ", "ر", "ى", "ة", "و", "ز", "د", "ج"],
];

const numberLayout = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  [".", "0", "%"],
];

const symbolLayout = [
  ["!", "@", "#", "$", "%", "^", "&", "*", "(", ")"],
  ["-", "_", "=", "+", "[", "]", "{", "}", "\\", "|"],
  [";", ":", "'", '"', ",", ".", "<", ">", "/", "?"],
];

const LETTERS_MODE_EXTRA_CHAR = "?";

type Language = "hebrew" | "english" | "arabic" | "russian";
type KeyboardMode = "letters" | "numbers" | "symbols";

interface Suggestion {
  text: string;
  type: "word" | "phrase";
  isNextWord?: boolean;
  isPhrasePair?: boolean;
}

interface YouTubeSearchKeyboardProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: () => void;
  className?: string;
}

export function YouTubeSearchKeyboard({
  value,
  onChange,
  onSearch,
  className,
}: YouTubeSearchKeyboardProps) {
  const { t, isRTL, lang } = useTranslation();
  const isUIHebrew = lang === "he" || lang === "ar";
  // Multi-language labels
  const YT_LABELS: Record<string, Record<string, string>> = {
    deleteWord:  { he: "מחק מילה", en: "Del Word", ru: "Удал. слово", ar: "حذف كلمة" },
    clear:       { he: "נקה", en: "Clear", ru: "Очистить", ar: "مسح" },
    deleteChar:  { he: "מחק תו", en: "Del Char", ru: "Очистить", ar: "حذف حرف" },
    space:       { he: "רווח", en: "Space", ru: "Пробел", ar: "مسافة" },
    placeholder: { he: "חיפוש ביוטיוב...", en: "Search YouTube...", ru: "Поиск в YouTube...", ar: "ابحث في يوتيوب..." },
  };
  const ytLabel = (key: string) => YT_LABELS[key]?.[lang] || YT_LABELS[key]?.en || key;
  const [language, setLanguage] = useState<Language>(lang === "he" ? "hebrew" : lang === "ar" ? "arabic" : lang === "ru" ? "russian" : "english");
  const [mode, setMode] = useState<KeyboardMode>("letters");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const engineRef = useRef(getPredictionEngine());
  const previousWordRef = useRef<string | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  // Refs for magnetism registration
  const keyRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const magnetIdsRef = useRef<Set<string>>(new Set());

  const isEnglishKeyboard = language === "english" && mode === "letters";

  const getActiveLayout = () => {
    if (mode === "letters") {
      if (language === "hebrew") return hebrewLayout;
      if (language === "arabic") return arabicLayout;
      if (language === "russian") return russianLayout;
      return englishLayout;
    }
    if (mode === "numbers") return numberLayout;
    if (mode === "symbols") return symbolLayout;
    if (language === "hebrew") return hebrewLayout;
    if (language === "arabic") return arabicLayout;
    if (language === "russian") return russianLayout;
    return englishLayout;
  };

  const activeLayout = getActiveLayout();

  // Register keyboard keys with PhysicsEngine for cursor magnetism
  const registerKey = useCallback((key: string, element: HTMLButtonElement | null) => {
    const magnetId = `yt-key-${key}`;
    if (element) {
      keyRefs.current.set(key, element);
      if (!magnetIdsRef.current.has(magnetId)) {
        PhysicsEngine.register(magnetId, element);
        magnetIdsRef.current.add(magnetId);
      } else {
        PhysicsEngine.updateBounds(magnetId, element);
      }
    } else {
      keyRefs.current.delete(key);
      if (magnetIdsRef.current.has(magnetId)) {
        PhysicsEngine.unregister(magnetId);
        magnetIdsRef.current.delete(magnetId);
      }
    }
  }, []);

  // Cleanup magnetism on unmount
  useEffect(() => {
    return () => {
      magnetIdsRef.current.forEach(id => PhysicsEngine.unregister(id));
      magnetIdsRef.current.clear();
    };
  }, []);

  // Set cursor to first key position on mount/language/mode change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mode === "numbers" || mode === "symbols") {
        // For number/symbol mode, focus first key in layout
        const layout = mode === "numbers" ? numberLayout : symbolLayout;
        const firstKeyChar = layout[0][0];
        const firstEl = keyRefs.current.get(firstKeyChar);
        if (firstEl) {
          const rect = firstEl.getBoundingClientRect();
          CursorStore.setPosition(rect.left + rect.width / 2, rect.top + rect.height / 2);
        }
      } else {
        const layout = activeLayout;
        const firstRow = layout[0];
        const isRTLLang = language === "hebrew" || language === "arabic";
        const firstKeyChar = isRTLLang ? firstRow[firstRow.length - 1] : firstRow[0];
        const firstEl = keyRefs.current.get(firstKeyChar);
        if (firstEl) {
          const rect = firstEl.getBoundingClientRect();
          CursorStore.setPosition(rect.left + rect.width / 2, rect.top + rect.height / 2);
        }
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [language, mode]);

  // Update suggestions
  useEffect(() => {
    const engine = engineRef.current;
    if (!value.trim()) {
      setSuggestions([]);
      return;
    }

    const localResults = engine.getSuggestions(value);
    const localSuggestions: Suggestion[] = localResults.map((r) => ({
      text: r.word,
      type: "word" as const,
      isNextWord: r.isNextWord,
      isPhrasePair: r.isPhrasePair,
    }));
    setSuggestions(localSuggestions);

    const wordCount = value.trim().split(/\s+/).length;
    const debounceTimer = setTimeout(async () => {
      if (wordCount >= 3) {
        const aiResults = await engine.fetchSmartPredictions(value);
        if (aiResults.length > 0) {
          const seen = new Set<string>();
          const merged: Suggestion[] = [];
          for (const ai of aiResults) {
            if (!seen.has(ai.word)) {
              seen.add(ai.word);
              merged.push({ text: ai.word, type: "word", isNextWord: true });
            }
          }
          for (const local of localSuggestions) {
            if (!seen.has(local.text)) {
              seen.add(local.text);
              merged.push(local);
            }
          }
          setSuggestions(merged.slice(0, 6));
        }
      }
    }, 600);

    return () => clearTimeout(debounceTimer);
  }, [value]);

  const handleKeyPress = (key: string) => {
    onChange(value + key);
  };

  const handleBackspace = () => {
    onChange(value.slice(0, -1));
  };

  const handleDeleteWord = () => {
    const trimmed = value.trimEnd();
    const lastSpaceIndex = trimmed.lastIndexOf(" ");
    if (lastSpaceIndex >= 0) {
      onChange(trimmed.substring(0, lastSpaceIndex + 1));
    } else {
      onChange("");
    }
  };

  const handleSpace = () => {
    const words = value.trim().split(/\s+/);
    if (words.length > 0) {
      previousWordRef.current = words[words.length - 1];
    }
    onChange(value + " ");
  };

  const handleClear = () => {
    onChange("");
    setSuggestions([]);
    previousWordRef.current = null;
  };

  const handleSelectSuggestion = useCallback((suggestion: Suggestion) => {
    const engine = engineRef.current;
    const currentValue = valueRef.current;

    if (suggestion.isNextWord) {
      engine.onWordSelected(suggestion.text, currentValue);
      const trimmedValue = currentValue.trimEnd();
      onChange(trimmedValue + " " + suggestion.text + " ");
      previousWordRef.current = suggestion.text;
    } else {
      engine.onWordSelected(suggestion.text, currentValue);
      const trimmedValue = currentValue.trimEnd();
      const lastSpaceIndex = trimmedValue.lastIndexOf(" ");
      const baseText = lastSpaceIndex >= 0
        ? trimmedValue.substring(0, lastSpaceIndex + 1)
        : "";
      onChange(baseText + suggestion.text + " ");
      previousWordRef.current = suggestion.text;
    }
    setSuggestions([]);
  }, [onChange]);

  const toggleMode = (newMode: KeyboardMode) => {
    if (mode === newMode) {
      setMode("letters");
    } else {
      setMode(newMode);
    }
  };

  const getNativeLanguageLabel = (): string => {
    switch (lang) {
      case "he": return "עברית";
      case "ar": return "عربية";
      case "ru": return "Русский";
      default: return "EN";
    }
  };

  // Get native Language type from uiLanguage (supports all 4 languages)
  const nativeLanguage: Language = (() => {
    switch (lang) {
      case "en": return "english";
      case "ar": return "arabic";
      case "ru": return "russian";
      default: return "hebrew";
    }
  })();

  const isInLettersMode = mode === "letters";

  // Language toggle label:
  // - In letters mode: show destination language ("לאן תעבור")
  // - In other modes: show current language ("איפה אתה עכשיו")
  const languageToggleLabel = isInLettersMode
    ? (language === "english" ? getNativeLanguageLabel() : "EN")
    : (language === "english" ? "EN" : getNativeLanguageLabel());

  // Handle language button click:
  // - In letters mode: switch language
  // - In other modes: return to letters in the same language (no language switch)
  const handleLanguageButtonClick = () => {
    setMode("letters");
    if (isInLettersMode) {
      setLanguage((prev) => (prev === "english" ? nativeLanguage : "english"));
    }
  };

  const toolbarItems = [
    { id: "numbers" as KeyboardMode, label: "123" },
    { id: "symbols" as KeyboardMode, label: "@" },
  ];

  return (
    <div className={cn("flex-1 flex flex-col overflow-hidden", className)}>
      {/* ── Header: Search + text display + Del Word + Clear ── */}
      <header
        data-scan-row="ytkb-actions"
        className={cn(
          "flex items-stretch gap-2 mb-1 mt-1 px-4 rounded-xl",
          "flex-row"
        )}
        dir={isUIHebrew ? "rtl" : "ltr"}
      >
        {/* Right side (visually in Hebrew) - Search button */}
        <div className={cn("flex gap-2 shrink-0", isUIHebrew && "flex-row-reverse")}>
          <button
            ref={(el) => { if (el) registerKey('search-btn', el); }}
            onClick={() => { if (value.trim()) onSearch(); }}
            className={cn(
              "flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-xl cursor-pointer",
              "transition-colors duration-200",
              "bg-accent text-accent-foreground",
              "hover:bg-accent/90",
              !value.trim() && "opacity-50 pointer-events-none"
            )}
            style={{ width: "clamp(80px, 10vw, 140px)", minHeight: "clamp(60px, 8vh, 130px)" }}
            {...(value.trim() ? { 'data-scan-item': '' } : {})}
            data-scan-cursor-id="yt-search-btn"
            data-cursor-color="teal"
          >
            <Search style={{ width: "clamp(1.1rem, 2.8vh, 2.4rem)", height: "clamp(1.1rem, 2.8vh, 2.4rem)" }} className="flex-shrink-0" />
            <span style={{ fontSize: "calc(clamp(0.85rem, 2vh, 1.2rem) * var(--font-scale, 1))" }} className="font-medium">{t("search")}</span>
          </button>
        </div>

        {/* Text Display */}
        <div
          className="flex-1 px-4 rounded-xl bg-card border-2 border-border flex items-center overflow-y-auto overflow-x-hidden"
          style={{ minHeight: "clamp(60px, 8vh, 130px)" }}
          dir={value ? (language === "hebrew" ? "rtl" : "ltr") : (isUIHebrew ? "rtl" : "ltr")}
        >
          <div className="w-full text-start">
            {value ? (
              <span className="text-xl sm:text-2xl font-medium whitespace-pre-wrap break-words leading-relaxed">
                {value}
              </span>
            ) : (
              <span className="text-muted-foreground text-base sm:text-lg">
                {ytLabel("placeholder")}
              </span>
            )}
          </div>
        </div>

        {/* Left side - Delete Word + Clear buttons */}
        <div className={cn("flex gap-2 shrink-0", isUIHebrew && "flex-row-reverse")}>
          <button
            ref={(el) => { if (el) registerKey('delword-btn', el); }}
            onClick={() => { if (value) handleDeleteWord(); }}
            className={cn(
              "flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-xl cursor-pointer",
              "transition-colors duration-200",
              "border-[1.5px] border-border/60 bg-card shadow-[0_1px_4px_hsl(195_100%_11%/0.08)]",
              "text-foreground hover:bg-accent hover:text-accent-foreground",
              !value && "opacity-50 pointer-events-none"
            )}
            style={{ width: "clamp(80px, 10vw, 140px)", minHeight: "clamp(60px, 8vh, 130px)" }}
            {...(value ? { 'data-scan-item': '' } : {})}
          >
            <Delete style={{ width: "clamp(1.1rem, 2.8vh, 2.4rem)", height: "clamp(1.1rem, 2.8vh, 2.4rem)" }} className="flex-shrink-0 text-destructive" />
            <span style={{ fontSize: "calc(clamp(0.85rem, 2vh, 1.2rem) * var(--font-scale, 1))" }} className="font-medium">{ytLabel("deleteWord")}</span>
          </button>
          <button
            ref={(el) => { if (el) registerKey('clear-btn', el); }}
            onClick={() => { if (value) handleClear(); }}
            className={cn(
              "flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-xl cursor-pointer",
              "transition-colors duration-200",
              "border-[1.5px] border-destructive/30 bg-card shadow-[0_1px_4px_hsl(195_100%_11%/0.08)]",
              "hover:bg-destructive/10",
              !value && "opacity-50 pointer-events-none"
            )}
            style={{ width: "clamp(80px, 10vw, 140px)", minHeight: "clamp(60px, 8vh, 130px)" }}
            {...(value ? { 'data-scan-item': '' } : {})}
          >
            <Trash2 style={{ width: "clamp(1.1rem, 2.8vh, 2.4rem)", height: "clamp(1.1rem, 2.8vh, 2.4rem)" }} className="flex-shrink-0 text-destructive" />
            <span style={{ fontSize: "calc(clamp(0.85rem, 2vh, 1.2rem) * var(--font-scale, 1))" }} className="font-medium text-foreground">{ytLabel("clear")}</span>
          </button>
        </div>
      </header>

      {/* Keyboard content with padding */}
      <div className="flex-1 flex flex-col px-4 pb-0 overflow-hidden min-h-0">
        {/* Suggestions Row - visible in letters/symbols mode */}
        {mode !== "numbers" && (
          <div
            data-scan-row="ytkb-suggestions"
            className={cn(
              "flex items-center justify-center gap-2 px-2 rounded-xl flex-shrink-0",
              suggestions.length > 0
                ? "bg-[hsl(185_30%_90%)] dark:bg-secondary border border-[hsl(185_30%_80%)] dark:border-border"
                : "bg-muted/40 border border-border/40"
            )}
            dir="ltr"
            style={{ height: "clamp(40px, 6vh, 58px)" }}
          >
            {(language === "hebrew" ? [...suggestions.slice(0, 5)].reverse() : suggestions.slice(0, 5)).map(
              (suggestion, index) => {
                const originalIndex = language === "hebrew"
                  ? Math.min(4, suggestions.length - 1) - index
                  : index;
                return (
                  <motion.button
                    key={`${suggestion.text}-${index}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleSelectSuggestion(suggestion);
                    }}
                    type="button"
                    className={cn(
                      "px-4 py-2 rounded-xl font-medium transition-all h-full cursor-pointer",
                      "bg-card text-foreground hover:bg-muted/80 border-[1.5px] border-primary/30 shadow-[0_1px_3px_hsl(195_100%_11%/0.08)]",
                      "flex-1 max-w-[180px] flex items-center justify-center"
                    )}
                    data-scan-item
                    data-scan-cursor-id={originalIndex === 0 ? "yt-first-suggestion" : undefined}
                    {...(originalIndex === 0 ? { 'data-scan-cursor-level': 'row' } : {})}
                    data-scan-cursor-target="yt-search-btn"
                    style={{ fontSize: "calc(clamp(0.9rem, 2.2vh, 1.3rem) * var(--font-scale, 1))" }}
                  >
                    {suggestion.text}
                  </motion.button>
                );
              }
            )}
          </div>
        )}

        {/* Main Keyboard Area */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          <motion.div
            key={`keyboard-${mode}-${language}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex flex-col justify-between px-2 py-1 min-h-0"
            style={{ gap: "var(--kb-key-gap, 4px)" }}
          >
            {/* Keyboard Grid */}
            {(() => {
              const maxCols = Math.max(...activeLayout.map(r => r.length));
              return activeLayout.map((row, rowIndex) => {
                return (
                   <div
                     key={rowIndex}
                     data-scan-row={`ytkb-row-${rowIndex}`}
                     className="flex-1 flex justify-center items-stretch rounded-lg"
                     dir="ltr"
                     style={{ gap: "var(--kb-key-gap, 4px)", minHeight: "clamp(50px, 6vh, 120px)" }}
                  >
                    {row.map((key, keyIndex) => {
                      const isRTLLang = language === "hebrew" || language === "arabic";
                      const isFirstKey = rowIndex === 0 && (isRTLLang ? keyIndex === row.length - 1 : keyIndex === 0);
                      return (
                      <motion.button
                        key={key}
                        ref={(el) => registerKey(key, el)}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleKeyPress(key)}
                        className={cn(
                          "rounded-xl aspect-square h-full cursor-pointer",
                          "bg-card hover:bg-primary/20 border border-[#1E888F] hover:border-primary/50 transition-all",
                          "flex items-center justify-center font-bold text-foreground",
                          isEnglishKeyboard && "font-poppins"
                        )}
                        style={{
                          maxWidth: `${100 / maxCols}%`,
                          fontSize: 'calc(clamp(1.6rem, 3.8vh, 3.5rem) * var(--font-scale, 1) * var(--card-label-scale-cap, 1))',
                        }}
                        data-scan-item
                        {...(isFirstKey ? { 'data-scan-default': '', 'data-scan-cursor-level': 'row' } : {})}
                        data-scan-cursor-target="yt-first-suggestion"
                      >
                        {key}
                      </motion.button>
                      );
                    })}
                  </div>
                );
              });
            })()}

            {/* Bottom Action Row - Delete Char + Space + Punctuation */}
            <div
              data-scan-row="ytkb-bottom"
              className="flex-1 flex justify-center items-stretch rounded-lg min-h-0"
              dir="ltr"
              style={{ gap: "var(--kb-key-gap, 4px)" }}
            >
              <motion.button
                ref={(el: HTMLButtonElement | null) => registerKey('backspace', el)}
                whileTap={{ scale: 0.95 }}
                onClick={handleBackspace}
                className="rounded-xl h-full min-w-[clamp(5rem,16vh,16rem)] px-3 bg-card hover:bg-muted/80 border border-[#1E888F] transition-all flex flex-col items-center justify-center gap-1 p-2 cursor-pointer"
                data-scan-item
              >
                <Delete
                  style={{ width: "clamp(1.5rem, 3vh, 2.5rem)", height: "clamp(1.5rem, 3vh, 2.5rem)", flexShrink: 0 }}
                  className="text-destructive"
                />
                <span
                  style={{ fontSize: "calc(clamp(0.8rem, 2.5vh, 1.4rem) * var(--font-scale, 1) * var(--card-label-scale-cap, 1))" }}
                  className="font-medium text-foreground leading-tight text-center whitespace-nowrap"
                >
                  {ytLabel("deleteChar")}
                </span>
              </motion.button>

              <motion.button
                ref={(el: HTMLButtonElement | null) => registerKey('space', el)}
                whileTap={{ scale: 0.95 }}
                onClick={handleSpace}
                className="flex-1 max-w-[400px] rounded-xl bg-card hover:bg-muted/80 border border-[#1E888F] transition-all flex items-center justify-center font-medium h-full cursor-pointer"
                style={{ fontSize: "calc(clamp(1rem, 2.5vh, 2.2rem) * var(--font-scale, 1) * var(--card-label-scale-cap, 1))" }}
                data-scan-item
              >
                {ytLabel("space")}
              </motion.button>

              {mode !== "numbers" && (
                <>
                  <motion.button
                    ref={(el: HTMLButtonElement | null) => registerKey('dot', el)}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleKeyPress(".")}
                    className={cn(
                      "rounded-xl aspect-square h-full bg-card hover:bg-primary/20 border border-[#1E888F] hover:border-primary/50 transition-all flex items-center justify-center font-bold cursor-pointer",
                      "text-[clamp(1.5rem,40%,3rem)]"
                    )}
                    data-scan-item
                  >
                    .
                  </motion.button>
                  <motion.button
                    ref={(el: HTMLButtonElement | null) => registerKey('extra', el)}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleKeyPress(LETTERS_MODE_EXTRA_CHAR)}
                    className={cn(
                      "rounded-xl aspect-square h-full bg-card hover:bg-primary/20 border border-[#1E888F] hover:border-primary/50 transition-all flex items-center justify-center font-bold cursor-pointer",
                      "text-[clamp(1.5rem,40%,3rem)]"
                    )}
                    data-scan-item
                  >
                    {LETTERS_MODE_EXTRA_CHAR}
                  </motion.button>
                </>
              )}
            </div>
          </motion.div>
        </div>

        {/* Toolbar */}
        <div
          data-scan-row="ytkb-toolbar"
          className={cn(
            "flex items-stretch justify-center pt-1 pb-1 rounded-lg flex-shrink-0 kb-toolbar-elevated",
            isUIHebrew && "flex-row-reverse"
          )}
          dir="ltr"
          style={{ gap: "clamp(4px, 0.5vw, 8px)", height: "clamp(52px, 7vh, 72px)" }}
        >
          <Button
            variant="outline"
            onClick={handleLanguageButtonClick}
            className="flex items-center justify-center px-3 py-2 rounded-xl h-full flex-1"
            data-scan-item
          >
            <span className="text-lg font-medium whitespace-nowrap">{languageToggleLabel}</span>
          </Button>

          {toolbarItems.map((item) => {
            const isActive = mode === item.id;
            return (
              <Button
                key={item.id}
                variant={isActive ? "default" : "outline"}
                onClick={() => toggleMode(item.id)}
                className={cn(
                  "flex items-center justify-center px-3 py-2 rounded-xl h-full flex-1",
                  isActive && "bg-primary text-primary-foreground"
                )}
                data-scan-item
              >
                <span className="text-lg font-medium whitespace-nowrap">{item.label}</span>
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
